import React, { useState, useEffect } from 'react';
import api, { apiMediaUrl } from '../../api/api';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { scoreTone, PILL_LIGHT, PILL_DARK, BAR } from '../../utils/resumeReport';
import StatusBadge from './StatusBadge';
import AudioPlayer from './AudioPlayer';
import { categoryBreakdownRows } from './interviewShared';
import { pitchRating, pitchHeadline } from './pitchShared';

const toList = (v) => Array.isArray(v) ? v.filter(Boolean) : (typeof v === 'string' && v.trim() ? [v.trim()] : []);

export default function PitchDetailView({ session, onBack, onReturnToList, isDarkMode }) {
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [retryUnlocked, setRetryUnlocked] = useState(false);

  useEffect(() => {
    if (!session) return;
    let cancelled = false;
    setDetail(null);
    setDetailLoading(true);
    setDetailError('');
    // ASSUMED CONTRACT: GET /pitch/sessions/:id -> { session: {..., AnalyzedJSON, responseAudioURL, TranscriptText, SecondsSinceUpdate} }
    api.get(`/pitch/sessions/${session.id}`)
      .then((res) => { if (!cancelled) setDetail(res.data?.session || null); })
      .catch((err) => {
        if (cancelled) return;
        console.error('Error fetching pitch detail:', err);
        setDetailError("Could not load this pitch's details. Try again.");
      })
      .finally(() => { if (!cancelled) setDetailLoading(false); });
    return () => { cancelled = true; };
  }, [session]);

  // Retry on a still-processing row is only allowed once it has genuinely stalled
  // (>= 120s since its last status write). Unlock the button when that point is
  // reached while the detail page is open.
  useEffect(() => {
    setRetryUnlocked(false);
    const secs = Number(detail?.SecondsSinceUpdate);
    if (!Number.isFinite(secs)) return;
    if (secs >= 120) { setRetryUnlocked(true); return; }
    const t = setTimeout(() => setRetryUnlocked(true), (120 - secs) * 1000);
    return () => clearTimeout(t);
  }, [detail]);

  const heading = isDarkMode ? 'text-slate-100' : 'text-slate-900';
  const sub = isDarkMode ? 'text-slate-400' : 'text-slate-600';
  const body = isDarkMode ? 'text-slate-300' : 'text-slate-700';
  const card = isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200';
  const pill = (score100) => (isDarkMode ? PILL_DARK : PILL_LIGHT)[scoreTone(score100)];

  const handleRetry = async () => {
    onBack();
    try {
      // ASSUMED CONTRACT: POST /pitch/sessions/:id/retry
      await api.post(`/pitch/sessions/${session.id}/retry`);
    } catch (err) {
      console.log('Pitch retry failed', err);
    }
  };

  const overallFeedback = detail?.AnalyzedJSON?.overall_feedback;
  const overallScore100 = Number.isFinite(overallFeedback?.overall_score) ? Math.round(overallFeedback.overall_score) : null;
  const overallStrengths = toList(overallFeedback?.overall_strengths);
  const overallImprovements = toList(overallFeedback?.overall_improvement_focus);
  const categoryRows = categoryBreakdownRows(overallFeedback);

  const handleDownloadPdf = () => {
    const w = window.open('', '_blank');
    if (!w) return;
    const esc = (s) => String(s ?? '').replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]));
    const TONE = {
      green: { bg: '#d1fae5', fg: '#065f46', bar: '#10b981' },
      yellow: { bg: '#fef3c7', fg: '#92400e', bar: '#f59e0b' },
      red: { bg: '#ffe4e6', fg: '#9f1239', bar: '#f43f5e' },
      blue: { bg: '#dbeafe', fg: '#1e40af', bar: '#3b82f6' },
    };
    const tone = (score) => TONE[scoreTone(score)] || TONE.blue;
    const bar = (score) => score == null ? '' :
      `<div class="bar-bg"><div class="bar" style="width:${score}%;background:${tone(score).bar}"></div></div>`;
    const pillHtml = (score, text) => {
      const t = tone(score);
      return `<span class="pill" style="background:${t.bg};color:${t.fg}">${esc(text)}</span>`;
    };
    const bulletList = (items, markerColor) =>
      `<ul style="color:${markerColor}">${items.map((x) => `<li><span style="color:#1e293b">${esc(x)}</span></li>`).join('')}</ul>`;
    const categoryRowsHtml = categoryRows.map(({ label, score100 }) => `
      <div class="catrow">
        <div class="rowhead"><h3>${esc(label)}</h3>${score100 != null ? pillHtml(score100, `${score100}/100`) : ''}</div>
        ${bar(score100)}
      </div>`).join('');
    const t = tone(overallScore100);
    w.document.write(`<html><head><title>Elevator Pitch Analysis — ${esc(session.role)} — Pono Pilot</title>
      <style>
        *{-webkit-print-color-adjust:exact;print-color-adjust:exact}
        body{font-family:Arial,sans-serif;max-width:800px;margin:0 auto;padding:24px;color:#1e293b}
        h1{font-size:24px;margin-bottom:4px;color:#0f172a}
        h2{font-size:18px;margin:18px 0 8px}h3{font-size:15px;margin:0}
        .meta{color:#64748b;font-size:13px}
        .card{border:1px solid #e2e8f0;border-radius:12px;padding:16px;margin-bottom:12px}
        .overall{border-left:5px solid ${t.bar}}
        .score{font-size:36px;font-weight:800;color:${t.bar}}.score span{font-size:16px;color:#64748b}
        .pill{display:inline-block;padding:2px 10px;border-radius:99px;font-size:12px;font-weight:700}
        .rowhead{display:flex;justify-content:space-between;align-items:center;gap:8px;margin-bottom:6px}
        .label{text-transform:uppercase;font-size:10px;letter-spacing:1px;font-weight:700;margin:10px 0 2px;color:#64748b}
        .muted{color:#64748b;font-size:13px}
        .bar-bg{height:8px;border-radius:8px;background:#f1f5f9;margin:6px 0 10px}
        .bar{height:8px;border-radius:8px}
        .catrow{margin-bottom:14px}.catrow:last-child{margin-bottom:0}
        .catrow .rowhead{margin-bottom:4px}.catrow .bar-bg{margin:0}
        ul{margin:2px 0;padding-left:18px;font-size:13px}
        .footer{margin-top:32px;padding-top:16px;border-top:1px solid #e2e8f0;text-align:center;color:#94a3b8;font-size:11px}
        @media print{body{padding:12px}}
      </style></head><body>
      <h1>Elevator Pitch Analysis</h1>
      <p class="meta">${esc(session.role)} &middot; ${esc(session.date)}</p>
      <div class="card overall">
        <div class="score">${overallScore100 != null ? overallScore100 : '—'}<span>/100</span></div>
        ${pillHtml(overallScore100, pitchRating(overallScore100))}
        ${bar(overallScore100)}
        <p style="font-weight:700;margin-top:4px;color:#0f172a">${esc(pitchHeadline(overallScore100))}</p>
        ${overallFeedback?.overall_summary ? `<p>${esc(overallFeedback.overall_summary)}</p>` : ''}
      </div>
      ${overallStrengths.length ? `<h2 style="color:#059669">Where you stand out</h2>${bulletList(overallStrengths, '#065f46')}` : ''}
      ${overallImprovements.length ? `<h2 style="color:#b45309">Where to focus next</h2>${bulletList(overallImprovements, '#92400e')}` : ''}
      ${detail?.TranscriptText ? `<h2 style="color:#0f172a">Transcript</h2><p class="muted">${esc(detail.TranscriptText)}</p>` : ''}
      ${categoryRowsHtml ? `<h2 style="color:#0f172a">Category breakdown</h2><div class="card">${categoryRowsHtml}</div>` : ''}
      <div class="footer">Powered by Pono Pilot</div>
      </body></html>`);
    w.document.close();
    setTimeout(() => w.print(), 300);
  };

  return (
    <div className={`p-4 md:p-6 ${isDarkMode ? 'bg-slate-900' : 'bg-slate-100'}`}>
      <div className="flex items-center justify-between gap-3 mb-5">
        <button
          type="button"
          onClick={onReturnToList || onBack}
          className={`flex items-center gap-2 text-sm font-medium transition-colors ${isDarkMode ? 'text-slate-300 hover:text-white' : 'text-slate-600 hover:text-slate-900'}`}
        >
          <ArrowLeft size={16} />
          Back to History
        </button>

        {!detailLoading && !detailError && session.status === 'done' && detail && (
          <button
            type="button"
            onClick={handleDownloadPdf}
            className="shrink-0 px-4 py-2 rounded-lg text-sm font-medium bg-primary-600 text-white hover:bg-primary-700 transition-colors"
          >
            Download PDF
          </button>
        )}
      </div>

      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <h1 className={`min-w-0 text-xl sm:text-2xl md:text-3xl font-extrabold ${heading}`}>Elevator Pitch Analysis</h1>
          <StatusBadge status={session.status} isDarkMode={isDarkMode} />
        </div>

        {detailLoading && (
          <div className="flex items-center justify-center gap-2 py-16">
            <Loader2 size={20} className={`animate-spin ${sub}`} />
            <span className={`text-sm ${sub}`}>Loading pitch details...</span>
          </div>
        )}

        {!detailLoading && detailError && (
          <div className={`rounded-2xl border p-5 ${card}`}>
            <p className={`text-sm ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>{detailError}</p>
          </div>
        )}

        {!detailLoading && !detailError && session.status === 'error' && (
          <div className={`rounded-2xl border p-5 space-y-3 ${card}`}>
            <p className={`text-sm ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>
              Something went wrong while analyzing this pitch. You can retry, or record a new one.
            </p>
            <button
              type="button"
              onClick={handleRetry}
              className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${isDarkMode ? 'bg-slate-800 text-slate-200 hover:bg-slate-700' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
            >
              Retry
            </button>
          </div>
        )}

        {!detailLoading && !detailError && (session.status === 'transcribing' || session.status === 'loading') && (
          <div className={`rounded-2xl border p-8 flex flex-col items-center text-center gap-3 ${card}`}>
            <Loader2 size={32} className={`animate-spin ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} />
            <p className={`text-base font-bold ${heading}`}>Processing your pitch</p>
            <p className={`text-sm max-w-md ${body}`}>
              Your pitch is being scored right now. This usually takes a minute or two — check back and your full report will be here.
            </p>
            <button
              type="button"
              onClick={handleRetry}
              disabled={!retryUnlocked}
              title={retryUnlocked ? '' : 'Available once the pitch has been stuck for 2 minutes'}
              className={`mt-1 px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${
                retryUnlocked
                  ? (isDarkMode ? 'bg-slate-700 text-slate-200 hover:bg-slate-600' : 'bg-slate-100 text-slate-700 hover:bg-slate-200')
                  : 'opacity-50 cursor-not-allowed ' + (isDarkMode ? 'bg-slate-800 text-slate-500' : 'bg-slate-100 text-slate-400')
              }`}
            >
              Retry
            </button>
            {!retryUnlocked && (
              <p className={`text-xs ${sub}`}>Stuck? Retry unlocks after 2 minutes with no progress.</p>
            )}
          </div>
        )}

        {!detailLoading && !detailError && session.status === 'incomplete' && (
          <div className={`rounded-2xl border p-5 space-y-3 ${card}`}>
            <p className={`text-sm ${body}`}>
              You haven&apos;t recorded a take for this pitch yet — or your last take had no audible speech.
              Record it now to get your analysis.
            </p>
            <button
              type="button"
              onClick={() => window.open(`/pitchSession?session=${session.id}`, '_blank')}
              className="px-4 py-2 rounded-xl text-sm font-semibold bg-primary-600 hover:bg-primary-500 text-white transition-colors"
            >
              Record your pitch
            </button>
          </div>
        )}

        {!detailLoading && !detailError && session.status === 'done' && (
          <>
            {overallFeedback && (
              <div className={`rounded-2xl border p-5 md:p-6 ${card}`}>
                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                  <div className="shrink-0">
                    <div className={`text-4xl font-extrabold leading-none ${heading}`}>
                      {overallScore100 != null ? overallScore100 : '—'}
                      <span className={`text-lg font-semibold ${sub}`}>/100</span>
                    </div>
                    <span className={`inline-block mt-2 text-xs font-semibold px-2.5 py-1 rounded-full ${pill(overallScore100)}`}>
                      {pitchRating(overallScore100)}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <p className={`text-lg font-bold ${heading}`}>{pitchHeadline(overallScore100)}</p>
                    {overallFeedback.overall_summary && (
                      <p className={`text-sm mt-1 leading-relaxed ${body}`}>{overallFeedback.overall_summary}</p>
                    )}
                  </div>
                </div>
                <div className={`flex gap-8 mt-4 pt-4 border-t ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                  <div>
                    <p className={`text-xs font-semibold uppercase tracking-wide ${sub}`}>Pitch</p>
                    <p className={`text-xl font-bold ${heading}`}>{session.role}</p>
                  </div>
                  <div>
                    <p className={`text-xs font-semibold uppercase tracking-wide ${sub}`}>Date</p>
                    <p className={`text-xl font-bold ${heading}`}>{session.date}</p>
                  </div>
                </div>
              </div>
            )}

            {detail?.responseAudioURL && (
              <section className={`rounded-2xl border p-5 ${card}`}>
                <h2 className={`text-lg font-bold mb-3 ${heading}`}>Your recording</h2>
                <AudioPlayer src={apiMediaUrl(detail.responseAudioURL)} isDarkMode={isDarkMode} />
                {detail?.TranscriptText ? (
                  <div className={`mt-3 border-l-2 pl-3 ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                    <p className={`text-[10px] font-semibold uppercase tracking-wide mb-0.5 ${sub}`}>Transcript</p>
                    <p className={`text-xs leading-relaxed ${sub}`}>{detail.TranscriptText}</p>
                  </div>
                ) : (
                  <p className={`mt-3 italic text-xs ${sub}`}>Nothing to transcribe — no speech detected.</p>
                )}
              </section>
            )}

            {overallStrengths.length > 0 && (
              <section className={`rounded-2xl border p-5 ${card}`}>
                <h2 className={`text-lg font-bold mb-3 ${heading}`}>Where you stand out</h2>
                <ul className={`text-sm space-y-1.5 ${body}`}>
                  {overallStrengths.map((s, i) => <li key={i}>&#9656; {s}</li>)}
                </ul>
              </section>
            )}

            {overallImprovements.length > 0 && (
              <section className={`rounded-2xl border p-5 ${card}`}>
                <h2 className={`text-lg font-bold mb-3 ${heading}`}>Where to focus next</h2>
                <ul className={`text-sm space-y-1.5 ${body}`}>
                  {overallImprovements.map((s, i) => <li key={i}>&#9656; {s}</li>)}
                </ul>
              </section>
            )}

            {categoryRows.length > 0 && (
              <section className={`rounded-2xl border p-5 ${card}`}>
                <h2 className={`text-lg font-bold mb-4 ${heading}`}>Category breakdown</h2>
                <div className="space-y-4">
                  {categoryRows.map(({ key, label, score100 }) => {
                    const tone = scoreTone(score100);
                    return (
                      <div key={key}>
                        <div className="flex items-center justify-between gap-3 mb-1.5">
                          <span className={`text-sm font-semibold ${heading}`}>{label}</span>
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full shrink-0 ${pill(score100)}`}>
                            {score100 != null ? `${score100}/100` : '—'}
                          </span>
                        </div>
                        <div className={`h-2 rounded-full overflow-hidden ${isDarkMode ? 'bg-slate-700' : 'bg-slate-100'}`}>
                          <div className={`h-full rounded-full ${BAR[tone]}`} style={{ width: `${score100 ?? 0}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
}
